import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { mediaHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { parseResponse } from '@/lib/api/parse-response';
import { i18n } from '@/shared/i18n';
import { chunk } from '@/shared/utils/chunk';
import { trackCreativeUploads } from '../model/creative-uploads';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import {
  confirmedAssetsSchema,
  jobAssetUrlSchema,
  presignedAssetsSchema,
  type ConfirmedAsset,
  type PresignedAsset,
} from './schemas';

// eslint-disable-next-line i18next/no-literal-string -- HTTP auth scheme name, not copy
const security = [{ scheme: 'bearer', type: 'http' } as const];

/**
 * What a platform will take as a creative, by extension.
 *
 * Read off the name because a browser reports no MIME type for some files and the wrong one
 * for others, and the presign endpoint validates against a closed list either way.
 */
const CONTENT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};

export function creativeContentTypeOf(file: File): string | undefined {
  return CONTENT_TYPES[file.name.split('.').pop()?.toLowerCase() ?? ''];
}

/**
 * The same list as an `accept` attribute, so a picker offers exactly what the presign
 * endpoint will mint a target for. Derived rather than written out again: a type added to
 * {@link CONTENT_TYPES} that stayed invisible to the picker would pass every check and then
 * be impossible to choose.
 */
export const CREATIVE_EXTENSIONS = Object.keys(CONTENT_TYPES).map((extension) => `.${extension}`);

/**
 * Whether the video route is the one that takes this file.
 *
 * A picker offering both kinds has to know which one it just got, and reading the extension
 * a second time somewhere else is how the two lists drift apart. A file no platform takes
 * answers false and is refused by the image route, which is where that message lives.
 */
export function isVideoFile(file: File): boolean {
  return creativeContentTypeOf(file)?.startsWith('video/') ?? false;
}

/**
 * Uploads one file to the target media-api minted for it.
 *
 * Plain `fetch`, deliberately not `mediaHttp`: the target is object storage, and sending our
 * bearer token to it would leak a credential to a third party for no benefit - the signature
 * in `uploadFields` is the whole authorisation. The field order is also load-bearing; S3
 * requires every form field before the file.
 *
 * `entities/project-file` has an equivalent, which this cannot call: FSD forbids one entity
 * slice importing another. Both belong in `shared/`, which is a move across five call sites
 * rather than something to do from here.
 */
async function putToStorage(target: PresignedAsset, file: File): Promise<boolean> {
  const body = new FormData();
  for (const [name, value] of Object.entries(target.uploadFields)) {
    body.append(name, String(value));
  }
  body.append('file', file);

  try {
    const response = await fetch(target.uploadUrl, { method: 'POST', body });
    return response.ok;
  } catch {
    // A network failure is the same outcome as a refusal from here: the object did not land,
    // so it must not be confirmed.
    return false;
  }
}

async function presign(
  jobId: string,
  files: { fileName: string; contentType: string }[],
): Promise<PresignedAsset[]> {
  const response = await mediaHttp.post({
    responseType: 'json',
    security,
    url: `/api/v1/delivery-jobs/${jobId}/assets/upload-urls`,
    body: { files },
  });
  return parseResponse(presignedAssetsSchema, response);
}

async function confirm(
  jobId: string,
  assets: { fileName: string; assetId: string }[],
): Promise<ConfirmedAsset[]> {
  const response = await mediaHttp.post({
    responseType: 'json',
    security,
    url: `/api/v1/delivery-jobs/${jobId}/assets/confirm`,
    body: { assets },
  });
  return parseResponse(confirmedAssetsSchema, response);
}

/**
 * Uploads the creatives a plan names, straight to storage.
 *
 * Three steps: ask media-api for a target per file, POST each file to its target, then tell
 * media-api which ones landed. The bytes never pass through our API, which is what lets a
 * plan carry thirty megabytes of creatives without a request that size.
 *
 * Only what uploaded is confirmed. A file that failed is reported back rather than thrown,
 * because a dozen images over a bad connection routinely means one retry, not a lost batch.
 *
 * A plain function and not only a hook: at job creation the id does not exist until the
 * record has been written, so there is nothing to parameterise a hook with at render time.
 */
export async function uploadJobAssets(jobId: string, files: File[]): Promise<ConfirmedAsset[]> {
  const usable: { file: File; contentType: string }[] = [];
  const outcomes: ConfirmedAsset[] = [];

  for (const file of files) {
    const contentType = creativeContentTypeOf(file);
    if (contentType) usable.push({ file, contentType });
    else outcomes.push({ fileName: file.name, status: 'failed', reason: 'unsupportedType' });
  }

  const settle = trackCreativeUploads(jobId, usable.length);
  try {
    for (const batch of chunk(usable, MAX_ASSETS_PER_CALL)) {
      outcomes.push(...(await uploadOneCall(jobId, batch)));
    }
  } finally {
    // A count left behind would hold pre-flight on this job until the tab is reloaded.
    settle();
  }

  return outcomes;
}

/**
 * The refusals media-api writes for an operator to read.
 *
 * `reason` is one field carrying three vocabularies, not two: a code this module minted, a
 * sentence the server wrote to be read, and — from `confirmAssets`' own catch — whatever an
 * exception said. Confirming an asset reads the stored object back out of assets-api and
 * writes a row for it, so a 503, a missing key or a failed DNS lookup all reach that catch,
 * and anything not on this list used to be shown as advice: `hero.jpg — getaddrinfo
 * ENOTFOUND assets-api.internal`.
 *
 * An allowlist for that reason, matched by shape because one of the two carries the ceiling
 * it refused against. Their wording lives in media-api, in `assets/asset-key.ts` and
 * `delivery-jobs.service.ts`; a change there costs the operator the sentence and falls back
 * to the generic advice, which is the safe direction for this to fail in.
 */
const OPERATOR_REFUSALS = [
  /^This creative is larger than the .+ limit\.$/,
  /^This file is an image, but it was stored as a video\b/,
];

/** The server's own explanation for refusing a creative, when it wrote one for the operator. */
export function serverReasonOf(asset: ConfirmedAsset): string | undefined {
  const reason = asset.reason;
  return reason && OPERATOR_REFUSALS.some((refusal) => refusal.test(reason)) ? reason : undefined;
}

/**
 * What one presign or confirm call will take, mirroring media-api's own
 * `MAX_ASSETS_PER_CALL` (`media-delivery/dto/job-assets.dto.ts`, enforced with
 * `@ArrayMaxSize` on both routes).
 *
 * A plan naming a whole folder of creatives is the case this exists for: sent as one call
 * it comes back 400, and every file in it then reads as failed for a reason nothing states.
 * The calls run one after another so that a chunk which is refused says nothing about the
 * chunks that already stored; the PUTs inside a chunk are still parallel, and the transfer
 * is bandwidth-bound rather than round-trip-bound anyway.
 */
const MAX_ASSETS_PER_CALL = 100;

/**
 * One presign, its uploads, and its confirm.
 *
 * Never rejects: a call that does not happen at all — an expired session, a refused
 * request, storage unreachable — is reported as its own files failing, so a later chunk
 * that stored is not misreported along with it. The cause is captured because nothing
 * else records it; the toast the operator sees cannot say what a 401 was.
 */
async function uploadOneCall(
  jobId: string,
  batch: { file: File; contentType: string }[],
): Promise<ConfirmedAsset[]> {
  try {
    return await sendOneCall(jobId, batch);
  } catch (cause: unknown) {
    Sentry.captureException(cause, { tags: { jobId } });
    return batch.map(({ file }) => ({
      fileName: file.name,
      status: 'failed',
      reason: 'batchFailed',
    }));
  }
}

async function sendOneCall(
  jobId: string,
  batch: { file: File; contentType: string }[],
): Promise<ConfirmedAsset[]> {
  const targets = await presign(
    jobId,
    batch.map(({ file, contentType }) => ({ fileName: file.name, contentType })),
  );

  const landed = await Promise.all(
    targets.map(async (target, index) => ({
      target,
      file: batch[index].file,
      ok: await putToStorage(target, batch[index].file),
    })),
  );

  const uploaded = landed.filter((entry) => entry.ok);
  const failed: ConfirmedAsset[] = landed
    .filter((entry) => !entry.ok)
    .map((entry) => ({
      fileName: entry.file.name,
      status: 'failed',
      reason: 'uploadFailed',
    }));

  const confirmed =
    uploaded.length > 0
      ? await confirm(
          jobId,
          uploaded.map((entry) => ({
            fileName: entry.target.fileName,
            assetId: entry.target.assetId,
          })),
        )
      : [];

  // Every outcome is named by the file the operator chose rather than by the asset: both
  // routes name one with media-api's `assetKey`, which lower-cases it, so a creative refused
  // at confirm came back as `hero_9x16.jpg` — a name they see neither in the dialog nor in
  // their folder. Confirm answers one entry per asset sent and in that order, the same
  // pairing `landed` already takes for granted with the presign targets. The response
  // carries no id to match on instead; should the counts ever disagree, the server's name
  // is the wrong case rather than the wrong file.
  const named =
    confirmed.length === uploaded.length
      ? confirmed.map((outcome, index) => ({ ...outcome, fileName: uploaded[index].file.name }))
      : confirmed;

  return [...named, ...failed];
}

/**
 * The same upload, wired to the cache.
 *
 * Everything a component needs beyond {@link uploadJobAssets} itself: the plan is
 * invalidated afterwards because a confirmed creative changes the row that names it.
 */
export function useUploadJobAssets(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (files: File[]) => uploadJobAssets(jobId, files),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.all }),
  });
}

/**
 * Attaches, replaces or removes one asset on one creative.
 *
 * A row at a time, for the case a bulk upload does not cover: one asset arrives late, or the
 * wrong crop went in. Uses the same presign-and-upload path rather than a second copy of it,
 * so the S3 field ordering lives in one place.
 *
 * The whole plan is invalidated afterwards rather than the node patched in place: the server
 * may have stored the file under a different name than it was given, and the pre-flight
 * finding about a missing file lives on the node too.
 */
function useSetNodeAsset(jobId: string, kind: 'image' | 'video' | 'logo') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId, file }: { nodeId: string; file: File | null }) => {
      const url = `/api/v1/delivery-jobs/${jobId}/nodes/${nodeId}/${kind}`;

      if (file === null) {
        await mediaHttp.delete({ responseType: 'json', security, url });
        return;
      }

      const contentType = creativeContentTypeOf(file);
      if (!contentType) {
        throw new Error(
          i18n.t('projects:mediaCampaign.fileTypeNotAccepted', { fileName: file.name }),
        );
      }

      const [target] = await presign(jobId, [{ fileName: file.name, contentType }]);
      if (!(await putToStorage(target, file))) {
        throw new Error(i18n.t('projects:mediaCampaign.fileUploadFailed', { fileName: file.name }));
      }

      await mediaHttp.put({
        responseType: 'json',
        security,
        url,
        body: { fileName: file.name, assetId: target.assetId },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.all }),
  });
}

export function useSetNodeImage(jobId: string) {
  return useSetNodeAsset(jobId, 'image');
}

export function useSetNodeVideo(jobId: string) {
  return useSetNodeAsset(jobId, 'video');
}

export function useSetNodeLogo(jobId: string) {
  return useSetNodeAsset(jobId, 'logo');
}

/**
 * Where to fetch the creative a plan row names, as something an `<img>` or a `<video>` can show.
 *
 * A signed storage url rather than bytes through our API: neither tag can carry a bearer
 * token, so this asks the authenticated route for the url and hands that to the tag. Nothing
 * is cached as an object URL any more, which also removes a blob held for the life of the tab.
 */
/**
 * The kind of file the naming column takes. A cell may name a file without its extension, and
 * a video row naming `hero` for both its video and its thumbnail means two different files.
 */
export type JobAssetKind = 'image' | 'video';

export function useJobAsset(
  jobId: string | undefined,
  fileName: string | null | undefined,
  kind?: JobAssetKind,
) {
  return useQuery({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobAsset(jobId ?? '', fileName ?? '', kind),
    queryFn: async () => {
      const response = await mediaHttp.get({
        responseType: 'json',
        security,
        // The global interceptor toasts every problem-details response, persistently. A
        // plan naming a creative nobody uploaded is the ordinary case here, so without
        // this a review screen opens under one stuck toast per missing file, saying only
        // "Not found" and naming neither the file nor the fix. Pre-flight reports the
        // same gap against the row it belongs to, which is where it can be acted on.
        // Only that status: a signing or storage fault has no row to be reported against,
        // so it keeps the toast.
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: '404' },
        url: `/api/v1/delivery-jobs/${jobId as string}/assets/${encodeURIComponent(fileName as string)}${kind ? `?kind=${kind}` : ''}`,
      });
      return parseResponse(jobAssetUrlSchema, response).url;
    },
    enabled: Boolean(jobId && fileName),
    // The url carries an expiring signature, so unlike the bytes it replaced this cannot be
    // kept indefinitely. Well inside any signature lifetime, and a plan review is minutes.
    staleTime: 5 * 60 * 1000,
    // A missing image is a fact about the plan, not a blip worth three round trips.
    retry: false,
  });
}

/**
 * Why no url came back: the plan names a file the upload did not carry, or the fetch itself
 * failed.
 *
 * Both leave the query empty, and the suppressed toast makes the placeholder the only
 * surface either one reaches. Blaming the upload for a signing fault would send someone
 * off to re-upload files that are already there, so the two have to stay apart.
 */
export function jobAssetFailure(error: unknown): 'missing' | 'unavailable' | null {
  if (!error) return null;
  return axios.isAxiosError(error) && error.response?.status === 404 ? 'missing' : 'unavailable';
}
